import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useMessaging } from '../contexts/MessagingProvider';
import { useNotifications } from '../contexts/NotificationsProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import HomeIcon from './icons/HomeIcon';
import BellIcon from './icons/BellIcon';
import ChatBubbleIcon from './icons/ChatBubbleIcon';
import LogoutIcon from './icons/LogoutIcon';
import UserIcon from './icons/UserIcon';
import CalendarIcon from './icons/CalendarIcon';
import SparklesIcon from './icons/SparklesIcon';
import SearchIcon from './icons/SearchIcon';
import UsersIcon from './icons/UsersIcon';
import CloseIcon from './icons/CloseIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import type { LanguageCode } from '../i18n/translations';

const Header: React.FC = () => {
  const { currentUser, managedProfile, logout } = useAuth();
  const { isMobileMenuOpen, setIsMobileMenuOpen, handleNavigation, searchTerm, setSearchTerm } = useNavigation();
  const { unreadMessagesCount } = useMessaging();
  const { unreadNotificationsCount } = useNotifications();
  const { language, setLanguage, t } = useLanguage();
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isLangMenuOpen, setLangMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const displayUser = managedProfile || currentUser;
  
  const onToggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
      if (isMobileSearchOpen && searchInputRef.current) {
          searchInputRef.current.focus();
      }
  }, [isMobileSearchOpen]);
  
  if (!currentUser || !displayUser) {
    return null;
  }

  const tier = currentUser.subscriptionTier;
  const canAccessCalendar = tier?.includes('_pro');

  const navigateAndClose = (target: any, payload: any = {}) => {
      setProfileMenuOpen(false);
      handleNavigation(target, payload);
  };

  const handleCloseSearchOverlay = () => {
      setIsMobileSearchOpen(false);
  };

  const languages: { code: LanguageCode; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'it', label: 'Italiano' },
  ];

  return (
    <header className="bg-brand-surface border-b border-gray-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <button
              className="md:hidden flex items-center text-brand-orange"
              onClick={onToggleMobileMenu}
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
            >
                <img src="/logo.png" alt="beYourAMP" className="h-10 w-auto" />
            </button>

            <button className="hidden md:flex flex-shrink-0 items-center" onClick={() => handleNavigation('feed')}>
              <img src="/logo.png" alt="beYourAMP" className="h-10 w-auto" />
            </button>
          </div>
          
          <div className="hidden md:flex flex-1 justify-center px-8">
            <div className="relative w-full max-w-lg">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('SEARCH_PLACEHOLDER')}
                    className="w-full bg-gray-700 text-white rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-brand-orange"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SearchIcon className="h-5 w-5 text-brand-muted" />
                </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isMobileSearchOpen && (
                <div className="absolute inset-0 bg-brand-surface z-50 flex items-center px-4 md:hidden animate-in fade-in duration-200">
                    <SearchIcon className="h-5 w-5 text-brand-muted mr-2" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('SEARCH_PLACEHOLDER')}
                        className="flex-1 bg-transparent text-white focus:outline-none h-full font-bold"
                    />
                    <button onClick={handleCloseSearchOverlay} className="ml-2 bg-gray-800 p-2 rounded-full">
                        <CloseIcon className="h-5 w-5 text-white" />
                    </button>
                </div>
            )}
            
            <button 
                onClick={() => setIsMobileSearchOpen(true)}
                className="md:hidden p-2 text-brand-muted hover:text-white"
                aria-label="Search"
            >
                <SearchIcon className="h-6 w-6" />
            </button>

            <div className="hidden md:flex items-center space-x-2">
                <div className="relative" ref={langMenuRef}>
                    <button 
                        onClick={() => setLangMenuOpen(!isLangMenuOpen)}
                        className="flex items-center space-x-1 p-2 text-brand-muted hover:text-white rounded-lg transition-colors text-xs font-bold uppercase"
                    >
                        <span>{language}</span>
                        <ChevronDownIcon className={`h-3 w-3 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isLangMenuOpen && (
                        <div className="absolute right-0 mt-2 w-32 bg-brand-surface rounded-lg shadow-2xl py-2 border border-gray-700 z-[100] animate-in slide-in-from-top-2 duration-200">
                            {languages.map(lang => (
                                <button
                                    key={lang.code}
                                    onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                                    className={`w-full text-left px-4 py-2 text-xs font-bold hover:bg-gray-800 transition-colors ${language === lang.code ? 'text-brand-orange' : 'text-gray-200'}`}
                                >
                                    {lang.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <HeaderIcon 
                    icon={<HomeIcon className="h-6 w-6" />} 
                    onClick={() => handleNavigation('feed')}
                    ariaLabel="Home"
                />
                <HeaderIcon 
                    icon={<ChatBubbleIcon className="h-6 w-6" />} 
                    badgeCount={unreadMessagesCount} 
                    onClick={() => handleNavigation('messages')}
                    ariaLabel="Messages"
                />
                <HeaderIcon 
                    icon={<BellIcon className="h-6 w-6" />} 
                    badgeCount={unreadNotificationsCount} 
                    onClick={() => handleNavigation('notifications')}
                    ariaLabel="Notifications"
                />
            </div>
             
             <div className="w-px h-8 bg-gray-700 mx-2 hidden md:block"></div>

             <div className="relative" ref={profileMenuRef}>
                <button 
                    onClick={() => setProfileMenuOpen(prev => !prev)}
                    className="focus:outline-none rounded-full"
                >
                    <img src={displayUser.avatarUrl} alt={displayUser.name} className="h-9 w-9 rounded-full border border-gray-700 hover:border-brand-orange transition-colors" />
                </button>

                {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-brand-surface rounded-lg shadow-2xl py-2 border border-gray-700 z-[100] animate-in slide-in-from-top-2 duration-200">
                        <p className="px-4 py-2 text-[10px] text-brand-muted font-bold tracking-widest border-b border-gray-800 mb-1">Signed in as {currentUser.name}</p>
                        <button
                            onClick={() => navigateAndClose('profile', { user: currentUser })}
                            className="w-full text-left flex items-center px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-brand-orange transition-colors"
                        >
                            <UserIcon className="h-4 w-4 mr-3" />
                            {t('MY_PROFILE')}
                        </button>
                        {canAccessCalendar && (
                            <button
                                onClick={() => navigateAndClose('calendar')}
                                className="w-full text-left flex items-center px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-brand-orange transition-colors"
                            >
                                <CalendarIcon className="h-4 w-4 mr-3" />
                                {t('MY_CALENDAR')}
                            </button>
                        )}
                        <button
                            onClick={() => navigateAndClose('profileManager')}
                            className="w-full text-left flex items-center px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-brand-orange transition-colors"
                        >
                            <UsersIcon className="h-4 w-4 mr-3" />
                            {t('NAV_PROFILE_MANAGER')}
                        </button>
                        <div className="border-t border-gray-800 my-1" />
                        <button
                            onClick={() => navigateAndClose('settings')}
                            className="w-full text-left flex items-center px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-brand-orange transition-colors"
                        >
                            <UserIcon className="h-4 w-4 mr-3" />
                            {t('SETTINGS')}
                        </button>
                        <button
                            onClick={() => navigateAndClose('feedback')}
                            className="w-full text-left flex items-center px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 hover:text-brand-orange transition-colors"
                        >
                            <SparklesIcon className="h-4 w-4 mr-3" />
                            {t('SUGGEST_IMPROVEMENTS')}
                        </button>
                        <div className="border-t border-gray-800 my-1" />
                        <button
                            onClick={logout}
                            className="w-full text-left flex items-center px-4 py-2.5 text-sm text-red-400 hover:bg-gray-800 transition-colors"
                        >
                            <LogoutIcon className="h-4 w-4 mr-3" />
                            {t('LOGOUT')}
                        </button>
                    </div>
                )}
             </div>

          </div>
        </div>
      </div>
    </header>
  );
};

interface HeaderIconProps {
    icon: React.ReactNode;
    badgeCount?: number;
    onClick: () => void;
    ariaLabel: string;
}

const HeaderIcon: React.FC<HeaderIconProps> = ({ icon, badgeCount, onClick, ariaLabel }) => (
    <button onClick={onClick} className="relative p-2 text-brand-muted hover:text-white hover:bg-gray-700 rounded-full transition-colors" aria-label={ariaLabel}>
        {icon}
        {badgeCount && badgeCount > 0 && (
            <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-brand-orange text-white text-[10px] font-bold transform translate-x-1/4 -translate-y-1/4 flex items-center justify-center border border-brand-surface">
                {badgeCount > 9 ? '9+' : badgeCount}
            </span>
        )}
    </button>
);

export default Header;